
import Vue from 'vue';
import { mapState } from 'vuex';
import VersionSelector from './components/VersionSelector.vue';
import FeatureCard from './components/FeatureCard.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import GlassButton from '@/components/base/GlassButton.vue';
import { WhatsNewState } from './types';
import { WhatsNewUpdate } from "@run-diffusion/shared";
import { ROUTER } from '@/router/constants';
import { db } from '@/firebase';
import { getUtcMoment } from '@/utils';

export default Vue.extend({
	name: 'WhatsNewPage',
	metaInfo: {
		title: 'What\'s New',
	},
	components: {
		VersionSelector,
		FeatureCard,
		BaseButton,
		GlassButton
	},
	props: {
		// When true, displays the previewUpdate instead of fetched updates
		previewMode: {
			type: Boolean,
			default: false
		},
		// The update to preview when in preview mode
		previewUpdate: {
			type: Object as () => WhatsNewUpdate | null,
			default: null
		}
	},
	data(): WhatsNewState {
		return {
			selectedVersionIndex: 0,
			updates: [] as WhatsNewUpdate[],
			loading: true,
			error: null
		};
	},
	computed: {
		...mapState(['user']),
		isPreviewMode(): boolean {
			return this.previewMode && !!this.previewUpdate;
		},
		currentVersion(): WhatsNewUpdate | null {
			return this.updates.length > 0 ? this.updates[this.selectedVersionIndex] : null;
		},
		// Helper computed property to check if user is admin
		isAdmin(): boolean {
			return !!(this.user && this.user.isAdmin);
		},
		// Returns either the preview update or the current version from the list
		displayedVersion(): WhatsNewUpdate | null {
			return this.isPreviewMode ? this.previewUpdate : this.currentVersion;
		},
	},
	watch: {
		// If preview update changes, reset navigation
		previewUpdate() {
			if (this.isPreviewMode) {
				this.selectedVersionIndex = 0;
			}
		}
	},
	mounted() {
		// Check for preview data in route params
		if (this.$route.query.preview === 'true' && this.$route.params.previewData) {
			try {
				const previewData = JSON.parse(this.$route.params.previewData);
				this.handlePreviewData(previewData);
			} catch (error) {
				console.error('Error parsing preview data:', error);
				this.loading = false;
			}
		} else if (!this.isPreviewMode) {
			this.fetchUpdates();
		} else {
			this.loading = false;
		}
	},
	methods: {
		getUtcMoment,
		onVersionSelected(index: number) {
			this.selectedVersionIndex = index;
		},

		async fetchUpdates() {
			try {
				this.loading = true;
				// Get updates where publishedAt is not null and is in the past
				// index created: whatsNewUpdates: isPublished Ascending date Descending __name__ Descending
				const snapshot = await db
					.collection('whatsNewUpdates')
					.where('isPublished', '==', true)
					// Sort by date in descending order (newest first)
					.orderBy('date', 'desc')
					.limit(100)
					.get();

				this.updates = snapshot.docs.map(doc => {
					const data = doc.data() as WhatsNewUpdate;
					return {
						...data,
						id: doc.id
					};
				}).filter(update => update.publishedAt); // Extra filter to ensure publishedAt exists

				this.loading = false;
			} catch (error) {
				console.error('Error fetching updates:', error);
				this.error = error;
				this.loading = false;
			}
		},
		goToManageUpdates() {
			this.routerPush(this.$route, this.$router, { name: 'ManageUpdates' });
		},

		navigateToUpdate(index: number) {
			if (index >= 0 && index < this.updates.length) {
				this.selectedVersionIndex = index;
				// Scroll to top of the content for better UX
				this.$nextTick(() => {
					const contentElement = document.querySelector('.whats-new-content');
					if (contentElement) {
						contentElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
					}
				});
			}
		},

		// Exit preview mode - navigate back to manage page
		exitPreview() {
			this.routerPush(this.$route, this.$router, { name: 'ManageUpdates' });
		},

		/**
		 * Handle call to action button clicks for a specific update
		 * This method will execute the appropriate action for the given update ID
		 *
		 * HOW TO USE:
		 * 1. When creating a new update in ManageUpdatesPage, add text for the "Call-to-Action Button"
		 * 2. After the update is saved, note its ID in the database (visible in Firebase console)
		 * 3. Add a new handler here using the update ID as the key and a function that performs
		 *    the desired action when the button is clicked
		 *
		 * EXAMPLE:
		 * 'your-update-id-here': () => {
		 *   // Your custom code here
		 *   this.routerPush(this.$route, this.$router, { name: ROUTER.PAGE_ROUTE_NAME }); // Navigate to a page
		 *   this.$store.dispatch('someAction'); // Dispatch a Vuex action
		 *   window.open('https://example.com'); // Open external URL
		 * }
		 *
		 * @param updateId - The ID of the WhatsNewUpdate being displayed
		 */
		handleCallToAction(updateId: string) {
			if (this.isPreviewMode) {
				console.log('Preview mode: Call-to-action would trigger an action for:', updateId);
				return;
			}

			// Get the current update
			const currentUpdate = this.displayedVersion;

			// If the update has a specific action type and it's not custom, handle it directly
			if (currentUpdate && currentUpdate.callToActionType && currentUpdate.callToActionType !== 'custom') {
				switch (currentUpdate.callToActionType) {
					case 'go-to-tool':
						if (currentUpdate.callToActionToolId) {
							this.routerPush(this.$route, this.$router, {
								name: ROUTER.RUNNIT_REROUTE,
								query: {
									toolId: currentUpdate.callToActionToolId
								},
							});
						}
						return;
					// Additional action types can be added here in the future
				}
			}

			// For custom actions or fallback to existing behavior, use the ID map
			const actionHandlers: {[key: string]: Function} = {
				// Define custom handlers for specific update IDs
				// Example handlers - replace these with your actual implementation
				// These are just examples to show how to implement feature-specific actions
				'974d96f3-1cf4-4d82-88ca-80b9397401be': () => { // April 15th, 2025
					// Route to a specific runnit tool with a hardcoded runnitNodeDef ID
					this.routerPush(this.$route, this.$router, {
						name: ROUTER.RUNNIT_REROUTE,
						query: {
							toolId: 'mCElGKWzzUAMGjpAvPBI', // Kling 2.0 node def ID
						},
					});
				},
				'f99f0a12-7772-45b1-a6b4-943634e41da7': () => { // March 28th, 2025
					// Example: Navigate to a specific page
					this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_APP_LIBRARY });
				},
				'7be7a1ee-a9df-40b1-b766-d4e50127dd9b': () => { // February 27th, 2025
					this.routerPush(this.$route, this.$router, { name: ROUTER.RUNNITS_HOME });
				},
				'example-update-2': () => {
					// Example: Open a specific feature or modal
					this.$store.dispatch('openFeatureModal', { feature: 'newFeature' });
				},
				'example-update-3': () => {
					// Example: Trigger a tutorial
					this.$store.dispatch('startTutorial', { tutorialId: 'new-feature-tutorial' });
				}
			};

			// Check if we have a specific handler for this update
			if (actionHandlers[updateId]) {
				actionHandlers[updateId]();
			} else {
				// Default handler - you can customize this
				console.error(`Action for update ${updateId} would happen here. Define a custom handler for this update.`);
			}
		},
		// Handle preview data from route params
		handlePreviewData(data: WhatsNewUpdate) {
			// This is essentially enabling preview mode programmatically
			this.$nextTick(() => {
				// We need to use $set to ensure reactivity
				this.$set(this, 'previewUpdate', data);
				this.$set(this, 'previewMode', true);
				this.loading = false;
			});
		}
	}
});
